import { TextToSpeech } from '@capacitor-community/text-to-speech';
import type { AppLanguage } from '../i18n/types';
import { isCapacitorNative } from '../utils/isCapacitorNative';

export const ASSISTANT_SPEAK_STORAGE_KEY = 'ofrontend.assistantSpeakReplies';

type SpeechSynthesisLike = {
  speaking: boolean;
  pending: boolean;
  paused?: boolean;
  getVoices: () => SpeechSynthesisVoice[];
  cancel: () => void;
  speak: (utterance: SpeechSynthesisUtterance) => void;
  pause?: () => void;
  resume?: () => void;
  addEventListener?: (type: string, listener: () => void) => void;
};

let speakTimer: ReturnType<typeof setTimeout> | null = null;
let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
let voicesPrimed = false;
/** Chrome desktop blocks speak() until a user gesture unlocks autoplay. */
let speechUnlocked = false;
let pendingSpeakQueue: Array<{ text: string; language: AppLanguage }> = [];
let speakQueue: Array<{ text: string; language: AppLanguage }> = [];
let speakPumpActive = false;
/** Bumped whenever the queue is interrupted so stale onend/onerror callbacks ignore. */
let speakPumpGeneration = 0;
let nativeSpeakSeq = 0;

function getSpeechSynthesis(): SpeechSynthesisLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const synth = window.speechSynthesis;
  return synth ?? null;
}

function isAndroidUa(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /Android/i.test(navigator.userAgent);
}

/** Native TTS on Capacitor Android/iOS — WebView often has no speechSynthesis. */
function shouldUseNativeTts(): boolean {
  return isCapacitorNative();
}

/** Load voices early — Chrome desktop often returns [] until voiceschanged. */
export function primeAssistantSpeech(): void {
  if (shouldUseNativeTts()) {
    return;
  }
  const synth = getSpeechSynthesis();
  if (!synth || voicesPrimed) {
    return;
  }
  voicesPrimed = true;
  try {
    synth.getVoices();
    synth.addEventListener?.('voiceschanged', () => {
      synth.getVoices();
    });
  } catch {
    /* ignore */
  }
}

export function isAssistantSpeechSupported(): boolean {
  if (shouldUseNativeTts()) {
    return true;
  }
  return Boolean(getSpeechSynthesis() && typeof window.SpeechSynthesisUtterance === 'function');
}

export function readAssistantSpeakEnabled(): boolean {
  if (typeof localStorage === 'undefined') {
    return true;
  }
  try {
    const raw = localStorage.getItem(ASSISTANT_SPEAK_STORAGE_KEY);
    // Default on; only an explicit "0" turns spoken replies off.
    if (raw === null) {
      return true;
    }
    return raw === '1';
  } catch {
    return true;
  }
}

export function writeAssistantSpeakEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(ASSISTANT_SPEAK_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

function clearKeepAlive(): void {
  if (keepAliveTimer != null) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
}

export function stopAssistantSpeech(): void {
  if (speakTimer != null) {
    clearTimeout(speakTimer);
    speakTimer = null;
  }
  clearKeepAlive();
  speakQueue = [];
  pendingSpeakQueue = [];
  speakPumpActive = false;
  speakPumpGeneration += 1;
  nativeSpeakSeq += 1;
  if (shouldUseNativeTts()) {
    void TextToSpeech.stop().catch(() => {
      /* ignore */
    });
    return;
  }
  const synth = getSpeechSynthesis();
  if (!synth) {
    return;
  }
  try {
    synth.cancel();
  } catch {
    /* ignore */
  }
}

/**
 * Call from a click/tap (open assistant, send, mic, speaker toggle).
 * Unlocks Chrome desktop autoplay and flushes any reply waiting to be spoken.
 */
export function unlockAssistantSpeech(): void {
  speechUnlocked = true;
  if (shouldUseNativeTts()) {
    flushPendingSpeakQueue();
    return;
  }
  const synth = getSpeechSynthesis();
  if (!synth || !isAssistantSpeechSupported()) {
    return;
  }
  primeAssistantSpeech();
  try {
    // Warm the engine inside the user-gesture window (silent).
    const warm = new SpeechSynthesisUtterance(' ');
    warm.volume = 0;
    warm.rate = 2;
    synth.cancel();
    synth.speak(warm);
    synth.cancel();
  } catch {
    /* ignore */
  }
  flushPendingSpeakQueue();
}

function flushPendingSpeakQueue(): void {
  if (!readAssistantSpeakEnabled() || pendingSpeakQueue.length === 0) {
    pendingSpeakQueue = [];
    return;
  }
  const pending = pendingSpeakQueue;
  pendingSpeakQueue = [];
  for (const item of pending) {
    speakQueue.push(item);
  }
  pumpSpeakQueue();
}

/** Test-only: reset module unlock / queue state. */
export function resetAssistantSpeechForTests(): void {
  stopAssistantSpeech();
  voicesPrimed = false;
  speechUnlocked = false;
  pendingSpeakQueue = [];
  speakQueue = [];
  speakPumpActive = false;
  speakPumpGeneration = 0;
}

function localeForLanguage(language: AppLanguage): string {
  return language === 'he' ? 'he-IL' : 'en-US';
}

function pickVoice(
  voices: SpeechSynthesisVoice[],
  language: AppLanguage,
): SpeechSynthesisVoice | null {
  if (voices.length === 0) {
    return null;
  }
  const locale = localeForLanguage(language).toLowerCase();
  const langPrefix = language === 'he' ? 'he' : 'en';
  const matching = voices.filter(
    (voice) =>
      voice.lang.toLowerCase() === locale || voice.lang.toLowerCase().startsWith(langPrefix),
  );
  // Prefer local OS voices — Chrome “Google …” cloud voices often fail on first PC speak.
  const local =
    matching.find((voice) => voice.localService) ??
    voices.find((voice) => voice.localService && voice.lang.toLowerCase().startsWith(langPrefix));
  if (local) {
    return local;
  }
  return matching[0] ?? null;
}

function startDesktopKeepAlive(synth: SpeechSynthesisLike): void {
  clearKeepAlive();
  // Chrome desktop can stop mid-utterance (~15s). Skip on Android (pause = cancel).
  if (isAndroidUa()) {
    return;
  }
  keepAliveTimer = setInterval(() => {
    if (!synth.speaking) {
      clearKeepAlive();
      return;
    }
    try {
      synth.pause?.();
      synth.resume?.();
    } catch {
      clearKeepAlive();
    }
  }, 8000);
}

type SpeakOptions = {
  /** Speak even if not yet unlocked (caller already holds a user gesture). */
  force?: boolean;
  /**
   * Append after any in-flight speech instead of cancelling it.
   * Use for greeting → query echo → results so lines are not cut off.
   */
  enqueue?: boolean;
};

type SpeakComplete = () => void;

function candidateTtsLangs(language: AppLanguage): string[] {
  if (language === 'he') {
    // Android TTS engines vary: he-IL, he, legacy iw / iw-IL.
    return ['he-IL', 'he', 'iw-IL', 'iw'];
  }
  return ['en-US', 'en'];
}

function speakViaWebSynthesis(text: string, language: AppLanguage, onComplete?: SpeakComplete): void {
  const synth = getSpeechSynthesis();
  if (!synth || typeof window.SpeechSynthesisUtterance !== 'function') {
    onComplete?.();
    return;
  }
  primeAssistantSpeech();
  try {
    synth.cancel();
  } catch {
    /* ignore */
  }
  let finished = false;
  const finish = () => {
    if (finished) {
      return;
    }
    finished = true;
    clearKeepAlive();
    onComplete?.();
  };
  speakTimer = setTimeout(() => {
    speakTimer = null;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = localeForLanguage(language);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;
    const voice = pickVoice(synth.getVoices(), language);
    if (voice) {
      utterance.voice = voice;
    }
    utterance.onend = finish;
    utterance.onerror = finish;
    try {
      synth.speak(utterance);
      if (typeof synth.resume === 'function' && !isAndroidUa()) {
        synth.resume();
      }
      startDesktopKeepAlive(synth);
    } catch {
      finish();
    }
  }, 80);
}

async function resolveNativeTtsLang(language: AppLanguage): Promise<string> {
  const candidates = candidateTtsLangs(language);
  for (const lang of candidates) {
    try {
      const result = await TextToSpeech.isLanguageSupported({ lang });
      if (result.supported) {
        return lang;
      }
    } catch {
      /* plugin may not implement the probe on every platform */
    }
  }
  return candidates[0] ?? localeForLanguage(language);
}

function speakNative(text: string, language: AppLanguage, onComplete?: SpeakComplete): void {
  const seq = (nativeSpeakSeq += 1);
  void (async () => {
    const finishIfCurrent = () => {
      if (seq === nativeSpeakSeq) {
        onComplete?.();
      }
    };
    try {
      await TextToSpeech.stop();
      if (seq !== nativeSpeakSeq) {
        return;
      }
      const lang = await resolveNativeTtsLang(language);
      if (seq !== nativeSpeakSeq) {
        return;
      }
      try {
        await TextToSpeech.speak({
          text,
          lang,
          rate: 1,
          pitch: 1,
          volume: 1,
          category: 'playback',
          queueStrategy: 1,
        });
        finishIfCurrent();
        return;
      } catch {
        // Hebrew (or other) voice pack missing — prompt Android TTS data install once.
        if (language === 'he') {
          try {
            await TextToSpeech.openInstall();
          } catch {
            /* ignore */
          }
        }
      }
      // Some WebViews still expose speechSynthesis; use it if the native engine failed.
      if (seq === nativeSpeakSeq) {
        speakViaWebSynthesis(text, language, finishIfCurrent);
        return;
      }
    } catch {
      if (seq === nativeSpeakSeq) {
        speakViaWebSynthesis(text, language, finishIfCurrent);
        return;
      }
    }
    finishIfCurrent();
  })();
}

function beginUtterance(text: string, language: AppLanguage, onComplete: SpeakComplete): void {
  if (shouldUseNativeTts()) {
    speakNative(text, language, onComplete);
    return;
  }
  speakViaWebSynthesis(text, language, onComplete);
}

function pumpSpeakQueue(): void {
  if (speakPumpActive) {
    return;
  }
  const next = speakQueue.shift();
  if (!next) {
    return;
  }
  speakPumpActive = true;
  const generation = speakPumpGeneration;
  beginUtterance(next.text, next.language, () => {
    if (generation !== speakPumpGeneration) {
      return;
    }
    speakPumpActive = false;
    pumpSpeakQueue();
  });
}

/**
 * Speak text. By default cancels anything in flight.
 * Pass `{ enqueue: true }` to append after the current line (greeting → echo → results).
 */
export function speakAssistantReply(
  text: string,
  language: AppLanguage,
  options: SpeakOptions = {},
): void {
  const trimmed = text.trim();
  if (!trimmed || !isAssistantSpeechSupported()) {
    return;
  }

  if (!speechUnlocked && !options.force) {
    if (options.enqueue) {
      pendingSpeakQueue.push({ text: trimmed, language });
    } else {
      pendingSpeakQueue = [{ text: trimmed, language }];
    }
    return;
  }

  if (options.enqueue) {
    speakQueue.push({ text: trimmed, language });
    pumpSpeakQueue();
    return;
  }

  speakQueue = [];
  speakPumpActive = false;
  speakPumpGeneration += 1;
  if (shouldUseNativeTts()) {
    nativeSpeakSeq += 1;
    void TextToSpeech.stop().catch(() => {
      /* ignore */
    });
  } else {
    if (speakTimer != null) {
      clearTimeout(speakTimer);
      speakTimer = null;
    }
    clearKeepAlive();
    const synth = getSpeechSynthesis();
    try {
      synth?.cancel();
    } catch {
      /* ignore */
    }
  }

  speakQueue.push({ text: trimmed, language });
  pumpSpeakQueue();
}
